import type {SmartTimeFlightDetails} from './smartTimeTypes'

/**
 * DOMESTIC OR INTERNATIONAL — the one function `yyzArrivalEntries` needs from the mobile
 * app's flight subsystem, reimplemented here rather than dragged across.
 *
 * ── ⚠️ WHY THIS FILE EXISTS AT ALL ────────────────────────────────────────────────
 * `yyzArrivalEntries.ts` is a verbatim copy and must stay one — the booth coordinates in
 * it are compared byte for byte against the two mobile apps, because arrival is inferred
 * locally on each side rather than announced by anyone. Its one unresolvable import is
 * this function. The original is 321 lines pulling in a flight-lookup API client and an
 * Expo config module, which is an entire subsystem imported for a country comparison.
 *
 * The rule, quoted from the handoff: SAME COUNTRY ON BOTH LEGS IS DOMESTIC, ANYTHING ELSE
 * OR ANYTHING UNKNOWN IS INTERNATIONAL.
 *
 * ── ⚠️ THE UNKNOWN CASE IS NOT A COIN TOSS ────────────────────────────────────────
 * It decides which door a passenger is sent to. An international arrival walks further,
 * because customs sits well back from the kerb, so guessing International gives them the
 * longer route. Guessing wrong that way costs a walk that is longer than it needed to be;
 * guessing the other way sends them to a door they cannot come out of. Keep the bias.
 *
 * ── ⚠️ ONE PIECE OF THE ORIGINAL IS MISSING, DELIBERATELY ─────────────────────────
 * When the flight provider omits a country code, the app falls back to a static table of
 * Canadian airports keyed by IATA code. That table is not in the handoff bundle. Without
 * it, a flight whose provider gave no country code resolves as International rather than
 * being looked up \u2014 which is the same safe direction the rest of this function leans, so
 * the gap degrades rather than breaks. Worth closing if the table turns up.
 */

type Leg = SmartTimeFlightDetails['departure'] | SmartTimeFlightDetails['arrival']

function resolveLegCountry(leg: Leg | undefined): string | null {
    const code = leg?.airport?.countryCode?.trim().toUpperCase()
    return code || null
}

export function resolveFlightType(flight?: SmartTimeFlightDetails | null): 'Domestic' | 'International' {
    if (!flight) return 'International'
    const from = resolveLegCountry(flight.departure)
    const to = resolveLegCountry(flight.arrival)
    if (!from || !to) return 'International'
    return from === to ? 'Domestic' : 'International'
}

export default resolveFlightType
